/* ==========================================================================
   Epic Trace — formulaire produit
   Choix du format, code Epic Trace, gravure, validation client.
   ========================================================================== */
(function () {
  'use strict';

  function parseJSON(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function setFieldError(field, message) {
    if (!field) return;
    var wrapper = field.closest('.field') || field.closest('.perso') || field.parentElement;
    if (!wrapper) return;
    wrapper.classList.add('field--invalid');
    var slot = wrapper.querySelector('.field__error');
    if (slot && message) slot.textContent = message;
  }

  /* Codes des créations faites sans compte, gardés dans le navigateur (le plus récent
     d'abord, cinq au plus). Écrits par la page du configurateur (message de l'iframe) et
     à chaque projet retrouvé ici ; lus quand la fiche s'ouvre sans code dans l'URL. */
  var GUEST_CODES_KEY = 'et_guest_codes';
  var CODE_RE = /^ET-[0-9A-Z]{4}-[0-9A-Z]{4}$/;

  function readRememberedCodes() {
    try {
      var list = JSON.parse(window.localStorage.getItem(GUEST_CODES_KEY) || '[]');
      return Array.isArray(list) ? list.filter(function (code) { return CODE_RE.test(code); }) : [];
    } catch (error) {
      return [];
    }
  }

  function rememberCode(code) {
    if (!CODE_RE.test(code)) return;
    try {
      var list = readRememberedCodes().filter(function (entry) { return entry !== code; });
      list.unshift(code);
      window.localStorage.setItem(GUEST_CODES_KEY, JSON.stringify(list.slice(0, 5)));
    } catch (error) { /* stockage refusé : la saisie du code reste possible */ }
  }

  function clearFieldError(field) {
    if (!field) return;
    var wrapper = field.closest('.field') || field.closest('.perso') || field.parentElement;
    if (wrapper) wrapper.classList.remove('field--invalid');
  }

  /* La gravure laser ne connait que du texte : lettres, chiffres, ponctuation,
     symboles simples (+, °…). Les emojis et pictogrammes n'ont pas de forme
     gravee, donc ils sont retires a la saisie plutot que decouverts a l'atelier. */
  var ENGRAVING_FORBIDDEN;
  try {
    ENGRAVING_FORBIDDEN = new RegExp('[\\p{Extended_Pictographic}\\p{Cc}\\p{Cf}\\u20E3\\uFE0E\\uFE0F]', 'gu');
  } catch (error) {
    /* Moteur sans classes Unicode : on retire au moins les paires de substitution
       (tous les emojis y vivent) et la plomberie emoji (ZWJ, selecteurs). */
    ENGRAVING_FORBIDDEN = /[\uD800-\uDFFF\u200D\u20E3\uFE0E\uFE0F]/g;
  }
  function sanitizeEngraving(value) {
    return value.replace(ENGRAVING_FORBIDDEN, '');
  }

  /* Mise en forme d'un montant en centimes selon le format monetaire de la
     boutique (« €{{amount_with_comma_separator}} »…), pour les ecarts de
     prix calcules cote client. */
  function formatMoney(cents, format) {
    var pattern = format || '€{{amount_with_comma_separator}}';
    var amount;
    if (pattern.indexOf('no_decimals') !== -1) {
      amount = String(Math.round(cents / 100));
    } else {
      amount = (cents / 100).toFixed(2);
    }
    if (pattern.indexOf('comma_separator') !== -1) {
      amount = amount.replace('.', ',');
    }
    return pattern.replace(/\{\{\s*[a-z_]+\s*\}\}/, amount);
  }

  /* Le lien NFC est facultatif, mais s'il est saisi il doit être une URL
     web complète : la puce ne sait ouvrir que ça. */
  function looksLikeUrl(value) {
    var url;
    try {
      url = new URL(value);
    } catch (error) {
      return false;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return url.hostname.indexOf('.') > 0;
  }

  /* Les trois styles d'ecriture de la gravure — contrat partage avec le
     configurateur (parametre d'URL « gravfont ») et avec base.css (@font-face
     sur les memes familles, auto-hebergees dans les assets du theme). */
  var ENGRAVING_FONT_STACKS = {
    moderne: '"Assistant", "Segoe UI", sans-serif',
    classique: '"Playfair Display", Georgia, serif',
    manuscrite: '"Caveat", "Segoe Script", cursive'
  };

  function ProductForm(root) {
    this.root = root;
    this.form = root.querySelector('[data-product-form], form.product__form, form[action*="/cart/add"]');
    if (!this.form) return;

    this.config = parseJSON(root.querySelector('[data-product-config]')) || {};
    this.variants = parseJSON(root.querySelector('[data-variants-json]')) || [];

    this.idInput = this.form.querySelector('[data-variant-id]');
    this.priceEl = root.querySelector('[data-product-price]');
    this.compareEl = root.querySelector('[data-product-compare]');
    this.badgeEl = root.querySelector('[data-price-badge]');
    this.submit = this.form.querySelector('[data-add-to-cart]');
    this.submitText = this.form.querySelector('[data-add-to-cart-text]');
    this.optionInputs = Array.prototype.slice.call(this.form.querySelectorAll('[data-option-input]'));

    this.codeInput = this.form.querySelector('[data-code-input]');
    this.projectNameInput = this.form.querySelector('[data-project-name-input]');
    this.previewInput = this.form.querySelector('[data-preview-input]');
    this.codePicker = this.form.querySelector('[data-code-picker]');
    this.codeSelect = this.form.querySelector('[data-code-select]');
    this.codeEmpty = this.form.querySelector('[data-code-empty]');
    this.codeGuest = this.form.querySelector('[data-code-guest]');
    this.guestProject = this.form.querySelector('[data-code-guest-project]');
    this.codeManual = this.form.querySelector('[data-code-manual]');
    this.codeHint = this.form.querySelector('[data-code-hint]');
    this.engravingBlock = root.querySelector('[data-engraving-block]');
    this.engravingInputs = Array.prototype.slice.call(this.form.querySelectorAll('[data-engraving-line]'));
    this.engravingFontInputs = Array.prototype.slice.call(this.form.querySelectorAll('[data-engraving-font]'));
    this.engravingPreview = root.querySelector('[data-engraving-preview]');
    this.nfcBlock = root.querySelector('[data-nfc-block]');
    this.nfcLinkInput = this.form.querySelector('[data-nfc-link]');
    this.confirmInput = this.form.querySelector('[data-confirm-input]');

    this.prefillCode();
    this.bind();
    this.onOptionChange();
    this.loadProjects();
  }

  /* Le configurateur renvoie vers la fiche produit avec le code du projet
     dans l'URL (« ?code=ET-XXXX-XXXX ») : on preremplit le champ. S'il a aussi
     essaye la gravure sur l'apercu 3D, les deux lignes arrivent en « grav1 » /
     « grav2 » : on selectionne alors la variante Gravure et on preremplit les
     champs — le client reste libre de tout modifier ou de repasser sans gravure. */
  ProductForm.prototype.prefillCode = function () {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return;
    }

    if (this.codeInput && !this.codeInput.value) {
      var code = params.get('code') || params.get('properties[Epic Trace]') || params.get('properties[Code Epic Trace]');
      if (code) this.codeInput.value = code.trim().toUpperCase();
    }

    var grav1 = params.get('grav1');
    var grav2 = params.get('grav2');
    var gravfont = params.get('gravfont');
    if (grav1 || grav2) this.applyEngraving(grav1, grav2, gravfont);
  };

  /* L'option qui separe les variantes gravees des autres : sa position et ses
     deux valeurs (gravee, sans gravure). Deduites des variantes plutot que
     codees en dur, pour survivre a un reordonnancement des options dans
     l'Admin — et jamais la position du format, que le client choisit lui-meme. */
  ProductForm.prototype.findEngravingOption = function () {
    var engraved = this.variants.filter(function (variant) { return variant.engraving === true; });
    var plain = this.variants.filter(function (variant) { return variant.engraving !== true; });
    if (!engraved.length || !plain.length) return null;
    var optionCount = engraved[0].options.length;
    for (var p = 0; p < optionCount; p++) {
      var engravedValues = {};
      engraved.forEach(function (variant) { engravedValues[variant.options[p]] = true; });
      var values = Object.keys(engravedValues);
      var overlaps = plain.some(function (variant) { return engravedValues[variant.options[p]]; });
      if (values.length === 1 && !overlaps) {
        return { position: p, engravedValue: values[0], plainValue: plain[0].options[p] };
      }
    }
    return null;
  };

  ProductForm.prototype.checkOptionValue = function (position, value) {
    this.form.querySelectorAll('input[data-option-input]').forEach(function (input) {
      if (parseInt(input.getAttribute('data-option-position'), 10) - 1 === position) {
        input.checked = input.value === value;
      }
    });
  };

  /* Preselectionne la variante Gravure et remplit les deux lignes — depuis
     l'URL du configurateur ou depuis un projet choisi dans la liste. Le style
     d'ecriture suit quand il est connu (« gravfont » ou projet). Le client
     reste libre de tout modifier ou de repasser sans gravure. */
  ProductForm.prototype.applyEngraving = function (grav1, grav2, fontId) {
    var option = this.findEngravingOption();
    if (option) this.checkOptionValue(option.position, option.engravedValue);

    var max = this.config.engravingChars || 40;
    var self = this;
    [grav1, grav2].forEach(function (line, index) {
      var input = self.engravingInputs[index];
      if (!input) return;
      input.value = line ? sanitizeEngraving(line).slice(0, max) : '';
      self.updateCounter(input);
      clearFieldError(input);
    });
    if (fontId) this.checkEngravingFont(fontId);
    this.updatePreview();
  };

  /* Coche le style demande ; un id inconnu (vieux lien, valeur trafiquee)
     laisse la selection en place plutot que de tout decocher. */
  ProductForm.prototype.checkEngravingFont = function (fontId) {
    var match = this.engravingFontInputs.filter(function (input) {
      return input.getAttribute('data-font-id') === fontId;
    })[0];
    if (match) match.checked = true;
  };

  /* Le miroir : retour a la variante sans gravure, champs vides, style par defaut. */
  ProductForm.prototype.clearEngraving = function () {
    var option = this.findEngravingOption();
    if (option) this.checkOptionValue(option.position, option.plainValue);
    var self = this;
    this.engravingInputs.forEach(function (input) {
      input.value = '';
      self.updateCounter(input);
      clearFieldError(input);
    });
    this.engravingFontInputs.forEach(function (input, index) {
      input.checked = index === 0;
    });
    this.updatePreview();
  };

  ProductForm.prototype.bind = function () {
    var self = this;

    this.optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        self.onOptionChange();
      });
    });

    if (this.codeInput) {
      this.codeInput.addEventListener('input', function () {
        clearFieldError(self.codeInput);
        self.updateSubmitState();
      });
      this.codeInput.addEventListener('blur', function () {
        self.codeInput.value = self.codeInput.value.trim().toUpperCase();
        self.updateSubmitState();
      });
    }

    this.engravingInputs.forEach(function (input) {
      input.addEventListener('input', function () {
        var clean = sanitizeEngraving(input.value);
        if (clean !== input.value) {
          var caret = input.selectionStart;
          var removed = input.value.length - clean.length;
          input.value = clean;
          if (caret != null) {
            try { input.setSelectionRange(caret - removed, caret - removed); } catch (error) { /* type=email etc. */ }
          }
        }
        clearFieldError(input);
        self.updateCounter(input);
        self.updatePreview();
        self.updateSubmitState();
      });
      self.updateCounter(input);
    });

    this.engravingFontInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        self.updatePreview();
      });
    });

    if (this.nfcLinkInput) {
      this.nfcLinkInput.addEventListener('input', function () {
        clearFieldError(self.nfcLinkInput);
        self.updateSubmitState();
      });
      /* Une adresse collée sans schéma (« strava.com/… ») est complétée
         plutôt que rejetée. */
      this.nfcLinkInput.addEventListener('blur', function () {
        var value = self.nfcLinkInput.value.trim();
        if (value && !/^https?:\/\//i.test(value)) value = 'https://' + value;
        self.nfcLinkInput.value = value;
        self.updateSubmitState();
      });
    }

    if (this.confirmInput) {
      this.confirmInput.addEventListener('change', function () {
        clearFieldError(self.confirmInput);
        self.updateSubmitState();
      });
    }

    this.form.addEventListener('submit', function (event) {
      self.onSubmit(event);
    });
  };

  /* --- Projets du client --------------------------------------------------- */

  /* Pas de saisie libre : seuls les codes existants ont un sens. Le proxy dit
     qui est connecté et liste ses projets ; selon la réponse, on dévoile la
     liste, l'état « aucun projet » ou l'invitation à se connecter. */
  ProductForm.prototype.loadProjects = function () {
    if (!this.codeSelect || !this.codeInput || !this.config.projectsUrl) return;
    var self = this;
    var showGuest = function () {
      /* Un code arrivé par l'URL peut être celui d'une création faite sans compte :
         on la montre, plutôt que d'inviter à se connecter. */
      var current = (self.codeInput.value || '').trim().toUpperCase();
      /* Sans code dans l'URL (retour sur la fiche depuis le panier, le menu, une
         recherche…), on reprend la dernière création faite sans compte sur cet
         appareil, plutôt que de ne proposer que la connexion. */
      var candidates = current ? [current] : readRememberedCodes();
      var tryNext = function (index) {
        if (index >= candidates.length) {
          if (self.codeGuest) self.codeGuest.hidden = false;
          self.setupManualCode();
          return;
        }
        self.loadGuestProject(candidates[index], function (found) {
          if (!found) tryNext(index + 1);
        });
      };
      tryNext(0);
    };
    fetch(this.config.projectsUrl, { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        if (!data || !data.loggedIn) {
          showGuest();
          return;
        }
        if (!data.projects || !data.projects.length) {
          if (self.codeEmpty) self.codeEmpty.hidden = false;
          return;
        }
        self.buildProjectPicker(data.projects);
      })
      .catch(showGuest);
  };

  /* Saisie d'un code sans compte : même vérification que pour un code venu de l'URL. */
  ProductForm.prototype.setupManualCode = function () {
    var self = this;
    var box = this.codeManual;
    if (!box || box.dataset.ready) return;
    box.dataset.ready = '1';
    box.hidden = false;
    var input = box.querySelector('[data-code-manual-input]');
    var button = box.querySelector('[data-code-manual-submit]');
    var error = box.querySelector('[data-code-manual-error]');

    var submit = function () {
      var code = (input.value || '').trim().toUpperCase();
      error.hidden = true;
      if (!code) return;
      button.disabled = true;
      self.loadGuestProject(code, function (found) {
        button.disabled = false;
        if (!found) {
          error.hidden = false;
          return;
        }
        /* Trouvé : la création remplace l'invitation à se connecter. */
        box.hidden = true;
        if (self.codeGuest) self.codeGuest.hidden = true;
        clearFieldError(self.codeInput);
      });
    };
    button.addEventListener('click', submit);
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        submit();
      }
    });
  };

  /* Création enregistrée sans compte : le worker rend nom, aperçu et gravure d'après le code.
     `done(true)` quand elle existe (le code reste dans le champ), `done(false)` sinon. */
  ProductForm.prototype.loadGuestProject = function (code, done) {
    var self = this;
    if (!this.config.guestProjectUrl || !this.guestProject || !code) {
      done(false);
      return;
    }
    fetch(this.config.guestProjectUrl + '?code=' + encodeURIComponent(code))
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (project) {
        if (!project || !project.code) {
          done(false);
          return;
        }
        var key = project.code.toUpperCase();
        rememberCode(key);
        self.projectsByCode = self.projectsByCode || {};
        self.projectsByCode[key] = project;
        self.codeInput.value = key;

        var name = self.guestProject.querySelector('[data-guest-name]');
        var codeLabel = self.guestProject.querySelector('[data-guest-code]');
        var image = self.guestProject.querySelector('[data-guest-preview]');
        if (name) name.textContent = project.name || '';
        if (codeLabel) codeLabel.textContent = key;
        if (image && project.previewUrl) {
          image.src = project.previewUrl;
          image.hidden = false;
        }
        self.guestProject.hidden = false;

        self.applyProjectMeta(key);
        self.updateSubmitState();
        done(true);
      })
      .catch(function () {
        done(false);
      });
  };

  ProductForm.prototype.buildProjectPicker = function (projects) {
    var self = this;
    var select = this.codeSelect;
    select.innerHTML = '';

    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.text('code_pick');
    select.appendChild(placeholder);

    /* Nom et aperçu du projet, ajoutés à la ligne de panier avec le code. */
    this.projectsByCode = {};

    projects.forEach(function (project) {
      if (!project.code) return;
      self.projectsByCode[project.code.toUpperCase()] = project;
      var option = document.createElement('option');
      option.value = project.code.toUpperCase();
      var label = (project.name || project.code) + ' — ' + project.code;
      if (project.createdAt) {
        var date = new Date(project.createdAt);
        if (!isNaN(date.getTime())) label += ' · ' + date.toLocaleDateString('fr-FR');
      }
      option.textContent = label;
      select.appendChild(option);
    });

    /* Un code arrivé par l'URL (bouton « Commander » ou fin de création dans
       le configurateur) présélectionne le projet correspondant. */
    var current = (this.codeInput.value || '').trim().toUpperCase();
    /* Comparaison directe des valeurs plutot qu'un selecteur construit par
       concatenation : un code venu de l'URL avec un guillemet casserait
       querySelector (DOMException) et empecherait toute la liste de s'initialiser. */
    var matched = false;
    if (current) {
      for (var oi = 0; oi < select.options.length; oi++) {
        if (select.options[oi].value === current) {
          matched = true;
          break;
        }
      }
    }
    select.value = matched ? current : '';
    if (matched || !current) {
      this.codeInput.value = matched ? current : '';
      this.applyProjectMeta(this.codeInput.value);
    } else {
      /* Absent de la liste du compte : peut-être une création faite sans compte,
         avant la connexion. Sinon le code est écarté comme avant. */
      this.loadGuestProject(current, function (found) {
        if (found) return;
        self.codeInput.value = '';
        self.applyProjectMeta('');
        self.updateSubmitState();
      });
    }

    select.addEventListener('change', function () {
      self.codeInput.value = select.value;
      self.applyProjectMeta(select.value);
      clearFieldError(self.codeInput);
      self.updateSubmitState();
    });

    if (this.codePicker) this.codePicker.hidden = false;
    if (this.codeHint) this.codeHint.hidden = false;
    this.updateSubmitState();
  };

  /* Nom du projet et aperçu accompagnent le code dans la ligne de panier.
     Les champs vides restent désactivés : pas de propriété vide sur la commande. */
  ProductForm.prototype.applyProjectMeta = function (code) {
    var project = (this.projectsByCode && this.projectsByCode[(code || '').toUpperCase()]) || null;
    if (this.projectNameInput) {
      this.projectNameInput.value = (project && project.name) || '';
      this.projectNameInput.disabled = !this.projectNameInput.value;
    }
    if (this.previewInput) {
      this.previewInput.value = (project && project.previewUrl) || '';
      this.previewInput.disabled = !this.previewInput.value;
    }
    /* La perso suit le projet sélectionné : gravure récupérée (option + lignes)
       quand il en portait une, retirée quand il n'en a pas — sinon la gravure
       du projet précédent resterait accrochée au nouveau. On ne touche à rien
       quand aucun projet n'est choisi (retour au placeholder). */
    if (project) {
      if (project.engraving && (project.engraving.line1 || project.engraving.line2)) {
        this.applyEngraving(
          project.engraving.line1 || '',
          project.engraving.line2 || '',
          project.engraving.font || null
        );
      } else {
        this.clearEngraving();
      }
      this.onOptionChange();
    }
  };

  /* --- Verrouillage du bouton d'ajout ------------------------------------- */

  /* Meme regles que validate(), mais silencieuses : le bouton reste grise
     tant que le formulaire est incomplet, sans afficher d'erreurs. */
  ProductForm.prototype.isComplete = function () {
    if (!this.currentVariant || !this.currentVariant.available) return false;

    if (this.codeInput && this.config.codeRequired) {
      var code = this.codeInput.value.trim().toUpperCase();
      if (!code) return false;
      if (this.config.codePattern) {
        try {
          if (!new RegExp(this.config.codePattern).test(code)) return false;
        } catch (error) {
          /* motif invalide : on n'exige qu'un champ non vide */
        }
      }
    }

    if (this.engravingBlock && !this.engravingBlock.hidden) {
      var max = this.config.engravingChars || 40;
      var first = this.engravingInputs[0];
      if (first && !first.value.trim()) return false;
      for (var i = 0; i < this.engravingInputs.length; i++) {
        if (this.engravingInputs[i].value.trim().length > max) return false;
      }
    }

    if (this.nfcBlock && !this.nfcBlock.hidden && this.nfcLinkInput) {
      var link = this.nfcLinkInput.value.trim();
      if (link && !looksLikeUrl(link)) return false;
    }

    if (this.confirmInput && !this.confirmInput.checked) return false;

    return true;
  };

  ProductForm.prototype.updateSubmitState = function () {
    if (!this.submit) return;
    this.submit.disabled = !this.isComplete();
  };

  /* --- Variantes ---------------------------------------------------------- */

  ProductForm.prototype.selectedOptions = function () {
    var values = [];
    this.form.querySelectorAll('[data-option-input]:checked').forEach(function (input) {
      values[parseInt(input.getAttribute('data-option-position'), 10) - 1] = input.value;
    });
    // Selects (repli si le theme est configure sans pastilles)
    this.form.querySelectorAll('select[data-option-input]').forEach(function (select) {
      values[parseInt(select.getAttribute('data-option-position'), 10) - 1] = select.value;
    });
    return values;
  };

  ProductForm.prototype.findVariant = function (values) {
    return (
      this.variants.filter(function (variant) {
        return values.every(function (value, index) {
          return !value || variant.options[index] === value;
        });
      })[0] || null
    );
  };

  ProductForm.prototype.onOptionChange = function () {
    var values = this.selectedOptions();
    var variant = this.findVariant(values);

    this.currentVariant = variant;
    this.updateAvailability(values);
    this.updateOptionDeltas(values);

    if (!variant) {
      if (this.submit) {
        this.submit.setAttribute('aria-disabled', 'true');
        this.submit.disabled = true;
        if (this.submitText) this.submitText.textContent = this.text('unavailable');
      }
      return;
    }

    if (this.idInput) this.idInput.value = variant.id;

    var discounted = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (this.priceEl) this.priceEl.innerHTML = variant.price_formatted;
    if (this.compareEl) {
      if (discounted) {
        this.compareEl.innerHTML = '<del>' + variant.compare_at_price_formatted + '</del>';
        this.compareEl.hidden = false;
      } else {
        this.compareEl.innerHTML = '';
        this.compareEl.hidden = true;
      }
    }
    if (this.badgeEl) {
      if (discounted) {
        var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        var label = '−' + pct + ' %';
        if (this.config.discountLabel) label += ' · ' + this.config.discountLabel;
        this.badgeEl.textContent = label;
        this.badgeEl.hidden = false;
      } else {
        this.badgeEl.textContent = '';
        this.badgeEl.hidden = true;
      }
    }

    if (this.submit) {
      if (variant.available) {
        this.submit.removeAttribute('aria-disabled');
        if (this.submitText) this.submitText.textContent = this.text('add');
      } else {
        this.submit.setAttribute('aria-disabled', 'true');
        if (this.submitText) this.submitText.textContent = this.text('sold_out');
      }
    }

    this.togglePersonalisation(variant);
    this.updateUrl(variant);
    this.updateSubmitState();
  };

  /* Les ecarts « + X € » affiches sous les options (gravure, tag NFC) sont
     recalcules par rapport au premier choix de l'option (« sans »), les
     autres options fixees a la selection courante. Ils sont affiches HORS
     remise (prix barre quand il existe) : le supplement annonce reste rond
     et stable (+ 7 €, + 5 €), la remise s'applique sur le total en bas. */
  ProductForm.prototype.updateOptionDeltas = function (values) {
    var self = this;
    var groups = {};
    this.form.querySelectorAll('input[data-option-input]').forEach(function (input) {
      var index = parseInt(input.getAttribute('data-option-position'), 10) - 1;
      if (index < 1) return; /* le format affiche ses tailles, pas d'ecart */
      if (!groups[index]) groups[index] = [];
      groups[index].push(input);
    });
    Object.keys(groups).forEach(function (key) {
      var index = parseInt(key, 10);
      var inputs = groups[key];
      var baseProbe = values.slice();
      baseProbe[index] = inputs[0].value;
      var baseVariant = self.findVariant(baseProbe);
      inputs.forEach(function (input) {
        var swatch = input.closest('.swatch');
        var slot = swatch && swatch.querySelector('[data-option-delta]');
        if (!slot) return;
        var probe = values.slice();
        probe[index] = input.value;
        var variant = self.findVariant(probe);
        var listPrice = function (v) { return v.compare_at_price || v.price; };
        var delta = variant && baseVariant ? listPrice(variant) - listPrice(baseVariant) : 0;
        if (delta > 0) {
          slot.textContent = '+ ' + formatMoney(delta, self.config.moneyFormat);
          slot.hidden = false;
        } else {
          slot.textContent = '';
          slot.hidden = true;
        }
      });
    });
  };

  /* Les combinaisons inexistantes sont grisees plutot que masquees */
  ProductForm.prototype.updateAvailability = function (values) {
    var self = this;
    this.form.querySelectorAll('input[data-option-input]').forEach(function (input) {
      var position = parseInt(input.getAttribute('data-option-position'), 10) - 1;
      var probe = values.slice();
      probe[position] = input.value;
      var match = self.findVariant(probe);
      input.disabled = !match || !match.available;
    });
  };

  ProductForm.prototype.updateUrl = function (variant) {
    if (!variant || !window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  };

  /* --- Gravure ------------------------------------------------------------ */

  /* Le pack et les options simples sont portes par la variante :
     on active les champs correspondants et on desactive les autres pour
     qu'ils ne soient pas envoyes au panier. */
  ProductForm.prototype.togglePersonalisation = function (variant) {
    var wantsEngraving = variant.engraving === true;

    /* Les radios de style sont desactivees avec les lignes : une commande sans
       gravure ne doit porter aucune propriete « Police de gravure ». */
    this.setBlockActive(
      this.engravingBlock,
      this.engravingInputs.concat(this.engravingFontInputs),
      wantsEngraving
    );

    if (wantsEngraving) this.updatePreview();

    /* L'encart « Lien NFC » suit la variante avec tag, comme la gravure. */
    var nfcInputs = this.nfcLinkInput ? [this.nfcLinkInput] : [];
    this.setBlockActive(this.nfcBlock, nfcInputs, variant.nfc === true);
  };

  ProductForm.prototype.setBlockActive = function (block, inputs, active) {
    if (block) block.hidden = !active;
    inputs.forEach(function (input) {
      input.disabled = !active;
      if (!active) clearFieldError(input);
    });
  };

  ProductForm.prototype.updateCounter = function (input) {
    var counter = input.parentElement.querySelector('[data-char-count]');
    if (!counter) return;
    var max = parseInt(input.getAttribute('maxlength'), 10) || this.config.engravingChars || 40;
    var length = input.value.length;
    counter.textContent = length + ' / ' + max;
    counter.classList.toggle('char-count--over', length > max);
  };

  ProductForm.prototype.updatePreview = function () {
    if (!this.engravingPreview) return;
    var lines = this.engravingInputs
      .map(function (input) {
        return input.value.trim();
      })
      .filter(function (value) {
        return value.length > 0;
      });
    this.engravingPreview.innerHTML = lines
      .map(function (line) {
        return line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      })
      .join('<br>');

    /* L'apercu s'affiche dans le style choisi. L'attribut data-font laisse le
       CSS adapter le reste (la manuscrite perd les capitales forcees). */
    var checked = this.engravingFontInputs.filter(function (input) {
      return input.checked;
    })[0];
    var fontId = checked ? checked.getAttribute('data-font-id') : null;
    this.engravingPreview.style.fontFamily = (fontId && ENGRAVING_FONT_STACKS[fontId]) || '';
    if (fontId) {
      this.engravingPreview.setAttribute('data-font', fontId);
    } else {
      this.engravingPreview.removeAttribute('data-font');
    }
  };

  /* --- Validation --------------------------------------------------------- */

  ProductForm.prototype.text = function (key) {
    return (this.config.strings && this.config.strings[key]) || '';
  };

  ProductForm.prototype.validate = function () {
    var errors = [];

    // Code Epic Trace
    if (this.codeInput && this.config.codeRequired) {
      var code = this.codeInput.value.trim().toUpperCase();
      if (!code) {
        setFieldError(this.codeInput, this.text('code_missing'));
        errors.push(this.codeInput);
      } else if (this.config.codePattern) {
        var pattern;
        try {
          pattern = new RegExp(this.config.codePattern);
        } catch (error) {
          pattern = null;
        }
        if (pattern && !pattern.test(code)) {
          setFieldError(this.codeInput, this.text('code_invalid'));
          errors.push(this.codeInput);
        }
      }
    }

    // Gravure : la ligne 1 est obligatoire, et chaque ligne respecte la longueur
    if (this.engravingBlock && !this.engravingBlock.hidden) {
      var max = this.config.engravingChars || 40;
      var self = this;
      this.engravingInputs.forEach(function (input) {
        var value = input.value.trim();
        if (value.length > max) {
          setFieldError(input, self.text('engraving_too_long'));
          errors.push(input);
        }
      });
      var firstLine = this.engravingInputs[0];
      if (firstLine && !firstLine.value.trim()) {
        setFieldError(firstLine, this.text('engraving_missing'));
        errors.push(firstLine);
      }
    }

    // Lien NFC : facultatif, mais une saisie doit être une URL web valide
    if (this.nfcBlock && !this.nfcBlock.hidden && this.nfcLinkInput) {
      var link = this.nfcLinkInput.value.trim();
      if (link && !looksLikeUrl(link)) {
        setFieldError(this.nfcLinkInput, this.text('nfc_invalid'));
        errors.push(this.nfcLinkInput);
      }
    }

    // Validation explicite du visuel par le client
    if (this.confirmInput && !this.confirmInput.checked) {
      setFieldError(this.confirmInput, this.text('confirm_missing'));
      errors.push(this.confirmInput);
    }

    return errors;
  };

  /* Entonnoir de l'Atelier : « ajout au panier ». Même contrat que stats.js (aucun
     identifiant, aucune donnée de commande) ; jamais bloquant. */
  function trackFunnel(name) {
    if (/(?:^|;\s*)et_nostats=1(?:;|$)/.test(document.cookie)) return; // visites de l'équipe (voir stats.js)
    var body = JSON.stringify({ site: 'shop', name: name, path: location.pathname });
    try {
      if (navigator.sendBeacon) navigator.sendBeacon('https://sso.epic-trace.fr/stats/event', body);
      else fetch('https://sso.epic-trace.fr/stats/event', { method: 'POST', body: body, keepalive: true, mode: 'no-cors' });
    } catch (e) { /* rien */ }
  }

  ProductForm.prototype.onSubmit = function (event) {
    var errors = this.validate();

    if (errors.length) {
      event.preventDefault();
      var first = errors[0];
      var target = first.closest('.field') || first.closest('.perso') || first;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (first.focus) first.focus({ preventScroll: true });
      return;
    }

    if (this.submit && this.submit.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    /* Lien NFC laissé vide : le champ est retiré de l'envoi pour ne pas
       créer une propriété « Lien NFC » vide sur la commande. */
    if (this.nfcLinkInput && !this.nfcLinkInput.disabled && !this.nfcLinkInput.value.trim()) {
      this.nfcLinkInput.disabled = true;
    }

    // Ajout asynchrone si le tiroir panier est disponible
    var drawer = window.EpicTrace && window.EpicTrace.CartDrawer;
    if (!drawer || !document.querySelector('[data-cart-drawer]')) {
      // Envoi classique du formulaire : la page va changer, la balise part avant.
      trackFunnel('panier');
      return;
    }

    event.preventDefault();
    var self = this;
    /* La page reste affichée après l'ajout (tiroir panier) : le champ de
       lien NFC neutralisé pour l'envoi doit redevenir éditable. */
    var restoreNfcInput = function () {
      if (self.nfcLinkInput && self.nfcBlock && !self.nfcBlock.hidden) {
        self.nfcLinkInput.disabled = false;
      }
    };
    drawer.addFromForm(this.form, this.submit).then(function () {
      restoreNfcInput();
      trackFunnel('panier');
    }).catch(function (error) {
      restoreNfcInput();
      var message = (error && error.description) || self.text('cart_error');
      var slot = self.form.querySelector('[data-form-error]');
      if (slot) {
        slot.textContent = message;
        slot.hidden = false;
      } else {
        window.location.href = window.Shopify.routes.root + 'cart';
      }
    });
  };

  function init() {
    document.querySelectorAll('[data-product-root]').forEach(function (root) {
      new ProductForm(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
